import React, { useState, useMemo } from 'react';
import { User, Users } from 'lucide-react';
import PersonaHeader from './personas/PersonaHeader';
import PersonaCard from './personas/PersonaCard';
import PersonaWorldView from './personas/PersonaWorldView';
import { useKiprData } from '@/lib/kiprData';
import { generalWorldCatalogStatus, isLearnerKipPlayable } from '@/lib/catalogAvailability';
import type { CatalogAvailability, ClientProfile, Colleague, Guardian, Kip } from '@/types';

interface PersonaWorld {
  id: string;
  name: string;
  age: number;
  diagnosis: string;
  description: string;
  color: string;
  gradient: string;
  icon: React.ReactNode;
  guardian: Guardian | null | undefined;
  client: ClientProfile | null;
  colleague: Colleague | null;
  levels: PersonaLevel[];
  completed: number;
  total: number;
  type: 'client' | 'colleague' | 'general';
  catalogStatus: CatalogAvailability;
}

interface PersonaLevel {
  id: string;
  title: string;
  description: string;
  difficulty: 'Beginner' | 'Intermediate' | 'Advanced';
  duration: string;
  completed: boolean;
  locked: boolean;
  stars: number;
  kipId: string;
  category: string;
  availability: CatalogAvailability;
  colleagueId: string | null;
  guardianId: string | null;
}

const catalogRank = (s: CatalogAvailability): number => {
  if (s === 'available') return 0;
  if (s === 'coming_soon') return 1;
  return 2;
};

const PersonasPage: React.FC = () => {
  const [selectedWorld, setSelectedWorld] = useState<string | null>(null);
  const { guardians, kips, clients, colleagues, loading, error, isAdmin } = useKiprData();

  const personaWorlds = useMemo((): PersonaWorld[] => {
    const worlds: PersonaWorld[] = [];

    const attachLevels = (scenarioKips: Kip[], kipIndexBase = 0): PersonaLevel[] =>
      scenarioKips.map((kip, kipIndex) => ({
        id: kip.id,
        title: kip.title,
        description: kip.description,
        difficulty:
          kipIndexBase + kipIndex === 0 ? 'Beginner' : kipIndexBase + kipIndex === 1 ? 'Intermediate' : 'Advanced',
        duration: kip.duration,
        completed: kip.completed || false,
        locked: !isLearnerKipPlayable(kip, clients, colleagues, isAdmin),
        stars: kip.score || 0,
        kipId: kip.id,
        category: kip.category,
        availability: kip.availability,
        colleagueId: kip.colleagueId ?? null,
        guardianId: kip.guardianId ?? null,
      }));

    clients.forEach((client) => {
      const guardian = guardians.find((g) => g.id === client.guardianId);
      const clientKips = kips.filter((kip) => kip.client?.id === client.id);

      if (clientKips.length > 0) {
        const completedKips = clientKips.filter((kip) => kip.completed);

        const colorScheme = {
          color: 'from-blue-500 to-indigo-600',
          gradient: 'bg-gradient-to-br from-blue-500/20 to-indigo-600/20',
        };

        worlds.push({
          id: client.id,
          name: client.name,
          age: client.age,
          diagnosis: client.diagnosis,
          description: `Master techniques with ${client.name}, ${client.age} years old with ${client.diagnosis}`,
          color: colorScheme.color,
          gradient: colorScheme.gradient,
          icon: <User className="w-5 h-5" />,
          guardian,
          client,
          colleague: null,
          levels: attachLevels(clientKips),
          completed: completedKips.length,
          total: clientKips.length,
          type: 'client',
          catalogStatus: client.availability,
        });
      }
    });

    colleagues.forEach((colleague) => {
      const colleagueKips = kips.filter((kip) => kip.colleagueId === colleague.id);

      if (colleagueKips.length > 0) {
        const completedKips = colleagueKips.filter((kip) => kip.completed);

        const colorScheme = {
          color: 'from-emerald-500 to-teal-600',
          gradient: 'bg-gradient-to-br from-emerald-500/20 to-teal-600/20',
        };

        worlds.push({
          id: `colleague-${colleague.id}`,
          name: colleague.name,
          age: 0,
          diagnosis: colleague.title,
          description: `Professional collaboration with ${colleague.name}, ${colleague.title} at ${colleague.organization}`,
          color: colorScheme.color,
          gradient: colorScheme.gradient,
          icon: <Users className="w-5 h-5" />,
          guardian: null,
          client: null,
          colleague,
          levels: attachLevels(colleagueKips),
          completed: completedKips.length,
          total: colleagueKips.length,
          type: 'colleague',
          catalogStatus: colleague.availability,
        });
      }
    });

    const generalKips = kips.filter((kip) => !kip.client && !kip.colleagueId);
    if (generalKips.length > 0) {
      const completedGeneralKips = generalKips.filter((kip) => kip.completed);

      worlds.push({
        id: 'general',
        name: 'Professional Skills',
        age: 0,
        diagnosis: '',
        description: 'General ABA therapy skills and professional development',
        color: 'from-indigo-500 to-blue-600',
        gradient: 'bg-gradient-to-br from-indigo-500/20 to-blue-600/20',
        icon: <Users className="w-8 h-8" />,
        guardian: null,
        client: null,
        colleague: null,
        levels: attachLevels(generalKips),
        completed: completedGeneralKips.length,
        total: generalKips.length,
        type: 'general',
        catalogStatus: generalWorldCatalogStatus(generalKips),
      });
    }

    worlds.sort((a, b) => catalogRank(a.catalogStatus) - catalogRank(b.catalogStatus));
    return worlds;
  }, [guardians, kips, clients, colleagues, isAdmin]);

  if (selectedWorld) {
    const world = personaWorlds.find((w) => w.id === selectedWorld);
    if (!world) return null;

    return (
      <PersonaWorldView
        world={world}
        onBack={() => setSelectedWorld(null)}
        isAdmin={isAdmin}
        clients={clients}
        colleagues={colleagues}
      />
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 to-slate-100">
      <PersonaHeader />

      {error && (
        <div className="max-w-6xl mx-auto px-6 pt-6">
          <div className="bg-amber-50 border border-amber-200 text-amber-800 px-4 py-3 rounded-lg text-sm">{error}</div>
        </div>
      )}

      <div className="max-w-6xl mx-auto px-6 py-12">
        {loading && (
          <div className="bg-white p-6 rounded-2xl shadow-sm border border-[#DADADA] text-[#555555] mb-8">
            Loading persona worlds...
          </div>
        )}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
          {personaWorlds.map((world) => (
            <PersonaCard key={world.id} world={world} onClick={() => setSelectedWorld(world.id)} />
          ))}
        </div>
      </div>
    </div>
  );
};

export default PersonasPage;
